import React from 'react';
import { f1, hbarPath, textWidth } from './scale';
import { useWidth } from './useWidth';

/**
 * Οριζόντιες μπάρες (κατηγορίες): μία σειρά δεδομένων → ένα χρώμα· η πρώτη στον πλήρη τόνο. Φαρδύ:
 * όνομα | μπάρα + % | ποσό | τεμάχια σε μία γραμμή. Στενό (< 560px): όνομα και ποσό πάνω, μπάρα κάτω.
 */
export interface HBarRow {
  key: string;
  name: string;
  value: number;
  /** Ποσοστό που γράφεται δίπλα στη μπάρα. */
  share: number;
  /** Δεξιά: π.χ. «668,10 €». */
  valueLabel: string;
  /** Τελευταία στήλη: π.χ. «262 τεμ.». */
  extraLabel?: string;
  tip?: string;
}

/** Κάτω από αυτό το πλάτος: όνομα/ποσό πάνω, μπάρα κάτω (η δεξιά στήλη του φύλλου είναι ~530px στα 1366). */
export const NARROW_MIN = 480;

export const HBarList: React.FC<{ rows: HBarRow[]; shareLabel: (v: number) => string; ariaLabel: string }> = ({ rows, shareLabel, ariaLabel }) => {
  const [ref, width] = useWidth();
  const max = Math.max(0, ...rows.map(r => r.value)) || 1;
  const narrow = width < NARROW_MIN;

  if (narrow) {
    const rowH = 42;
    const barH = 8;
    const bw = Math.max(20, width - 46);
    return (
      <div ref={ref} className="xh-chart" data-testid="hbar-list">
        <svg width={width} height={Math.max(0, rows.length * rowH - 8)} role="img" aria-label={ariaLabel}>
          {rows.map((r, i) => {
            const y = i * rowH;
            const len = Math.max(2, (bw * r.value) / max);
            const by = y + 23;
            return (
              <g key={r.key}>
                {r.tip && <title>{r.tip}</title>}
                <rect className="xh-hit" x={0} y={y} width={width} height={rowH} />
                <text className="xh-axl xh-axl-name" x={0} y={y + 14}>{r.name}</text>
                <text className="xh-axl" x={width} y={y + 14} textAnchor="end">
                  {r.extraLabel && <tspan className="xh-axl-dim">{`${r.extraLabel} · `}</tspan>}
                  <tspan className="xh-axl-name">{r.valueLabel}</tspan>
                </text>
                <path className={`xh-bar ${i === 0 ? 'v1' : 'v2'}`} d={hbarPath(0, by, len, barH)} />
                <text className="xh-vl" x={f1(len + 8)} y={by + 8}>{shareLabel(r.share)}</text>
              </g>
            );
          })}
        </svg>
      </div>
    );
  }

  const longest = Math.max(0, ...rows.map(r => textWidth(r.name, 13, 400)));
  const nameW = Math.min(Math.round(width * 0.45), Math.ceil(longest) + 16);
  const valW = 84;
  const extraW = rows.some(r => r.extraLabel) ? 64 : 0;
  const bw = Math.max(40, width - nameW - valW - extraW - 60);
  const rowH = 30;
  const barH = 10;
  return (
    <div ref={ref} className="xh-chart" data-testid="hbar-list">
      <svg width={width} height={rows.length * rowH} role="img" aria-label={ariaLabel}>
        {rows.map((r, i) => {
          const y = i * rowH;
          const len = Math.max(2, (bw * r.value) / max);
          return (
            <g key={r.key}>
              {r.tip && <title>{r.tip}</title>}
              <rect className="xh-hit" x={0} y={y} width={width} height={rowH} />
              <text className="xh-axl xh-axl-name" x={0} y={y + 19}>{r.name}</text>
              <path className={`xh-bar ${i === 0 ? 'v1' : 'v2'}`} d={hbarPath(nameW, y + 10, len, barH)} />
              <text className="xh-vl" x={f1(nameW + len + 8)} y={y + 19}>{shareLabel(r.share)}</text>
              <text className="xh-axl xh-axl-name" x={width - extraW} y={y + 19} textAnchor="end">{r.valueLabel}</text>
              {r.extraLabel && <text className="xh-axl" x={width} y={y + 19} textAnchor="end">{r.extraLabel}</text>}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default HBarList;
